import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';
import withTranslations, { withTranslationsRaw } from '@wheelbox/lithejs-i18n';
import { QuoteIcon } from '@wheelbox/lithejs-lucide';

import { SectionHeading } from '@components/shared';
import {
  Card,
  CardContent,
  Avatar,
  AvatarFallback,
} from '@wheelbox/lithejs-ui';

export interface TestimonialsSectionProps {
  id?: string;
  testId?: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
}

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const TestimonialsSection: LitheFC<TestimonialsSectionProps> = ({
  id = 'testimonials-section',
  testId = 'testimonials-section',
}): LitheElement<'section'> => {
  const t = withTranslations('Home');
  const tRaw = withTranslationsRaw('Home');

  // Get testimonials from i18n (raw array)
  const testimonials = tRaw<Testimonial[]>('testimonials.items');

  // If no testimonials, don't render the section
  if (
    !testimonials ||
    !Array.isArray(testimonials) ||
    testimonials.length === 0
  ) {
    return ['section', { id, 'data-testid': testId, class: 'hidden' }];
  }

  return [
    'section',
    {
      id,
      'data-testid': testId,
      class: 'py-20 px-4',
    },
    [
      'div',
      { class: 'max-w-6xl mx-auto' },
      SectionHeading({
        id: `${id}-heading`,
        title: t('testimonials.title') as string,
        subtitle: t('testimonials.subtitle') as string,
      }),
      [
        'div',
        {
          class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
        },
        ...testimonials.map((testimonial, index) =>
          Card(
            {
              id: `${id}-card-${index}`,
              classes: 'h-full flex flex-col hover:shadow-lg transition-shadow',
            },
            CardContent(
              { classes: 'pt-6 flex-1 flex flex-col' },
              // Quote icon
              [
                'div',
                { class: 'mb-4' },
                QuoteIcon({
                  size: 24,
                  classes: 'text-primary/40',
                }),
              ],
              // Quote text
              [
                'blockquote',
                {
                  class:
                    'text-muted-foreground italic flex-1 mb-6 text-sm leading-relaxed',
                },
                `"${testimonial.quote}"`,
              ],
              // Author info
              [
                'div',
                { class: 'flex items-center gap-3 mt-auto' },
                Avatar(
                  { classes: 'h-10 w-10' },
                  AvatarFallback(
                    {
                      classes: 'bg-primary/10 text-primary text-xs font-medium',
                    },
                    getInitials(testimonial.author)
                  )
                ),
                [
                  'div',
                  {},
                  [
                    'p',
                    { class: 'font-medium text-foreground text-sm' },
                    testimonial.author,
                  ],
                  [
                    'p',
                    { class: 'text-xs text-muted-foreground' },
                    `${testimonial.role} @ ${testimonial.company}`,
                  ],
                ],
              ]
            )
          )
        ),
      ],
    ],
  ];
};

export default TestimonialsSection;
